import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Progress } from '../ui/progress';
import { 
  Monitor, 
  Cpu, 
  HardDrive, 
  MemoryStick, 
  Wifi, 
  Shield, 
  Settings, 
  Info,
  Computer,
  Zap,
  Thermometer,
  Clock
} from 'lucide-react';

export const SystemProperties: React.FC = () => {
  const [currentTime] = useState(new Date().toLocaleString());

  const systemInfo = {
    os: {
      name: 'MapleOS',
      version: '26.2',
      build: '2.8.0',
      architecture: 'WASM x64-based processor'
    },
    hardware: {
      processor: '11th Gen Intel(TM) Core(TM) i9-11900KF @ 3.50GHz',
      installedMemory: '32.0 GB (31.8 GB usable)',
      systemType: '64-bit Operating System, x64-based processor',
      penAndTouch: 'No Pen or Touch Input is available for this Display',
      computerName: 'MapleOS-PC',
      domain: 'WORKGROUP',
      description: 'Premium Gaming Workstation'
    },
    performance: {
      cpuUsage: 15,
      memoryUsage: 42,
      diskUsage: 65,
      temperature: 58,
      uptime: '2 days, 14 hours, 32 minutes'
    }
  };

  return (
    <div className="h-full flex flex-col bg-background min-h-0">
      <Tabs defaultValue="general" className="flex-1 flex flex-col min-h-0">
        <TabsList className="grid w-full grid-cols-4 bg-muted/50 flex-shrink-0">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Info size={16} />
            General
          </TabsTrigger>
          <TabsTrigger value="hardware" className="flex items-center gap-2">
            <Computer size={16} />
            Hardware
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-2">
            <Zap size={16} />
            Performance
          </TabsTrigger>
          <TabsTrigger value="advanced" className="flex items-center gap-2">
            <Settings size={16} />
            Advanced
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="h-full overflow-y-auto px-6 py-6 space-y-6">
          {/* General Tab */}
          <TabsContent value="general" className="space-y-6 mt-0">
            {/* OS Information Card */}
            <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Monitor size={32} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-2xl text-primary">{systemInfo.os.name}</CardTitle>
                    <CardDescription className="text-lg font-medium">
                      {systemInfo.os.version}
                    </CardDescription>
                    <Badge variant="secondary" className="mt-1">
                      Build {systemInfo.os.build}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Architecture:</span>
                    <div className="font-medium">{systemInfo.os.architecture}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Product ID:</span>
                    <div className="font-mono text-xs">{systemInfo.os.productId}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Registration Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield size={20} />
                  Registration Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Registered to:</span>
                    <div className="font-medium">{systemInfo.os.registeredTo}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Organization:</span>
                    <div className="font-medium">{systemInfo.os.organization}</div>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-2 text-green-600">
                  <Shield size={16} />
                  <span className="font-medium">MapleOS is activated</span>
                </div>
              </CardContent>
            </Card>

            {/* Computer Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Computer size={20} />
                  Computer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Computer name:</span>
                    <div className="font-medium">{systemInfo.hardware.computerName}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Workgroup:</span>
                    <div className="font-medium">{systemInfo.hardware.domain}</div>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Description:</span>
                    <div className="font-medium">{systemInfo.hardware.description}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hardware Tab */}
          <TabsContent value="hardware" className="space-y-6 mt-0">
            {/* Processor Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cpu size={20} />
                  Processor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="font-medium">{systemInfo.hardware.processor}</div>
                  <div className="text-muted-foreground">3.50 GHz • 8 Cores • 16 Logical Processors</div>
                </div>
              </CardContent>
            </Card>

            {/* Memory Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MemoryStick size={20} />
                  Memory (RAM)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="font-medium">{systemInfo.hardware.installedMemory}</div>
                  <div className="text-muted-foreground">DDR4-3200 • 4 x 8GB DIMMs</div>
                </div>
              </CardContent>
            </Card>

            {/* System Type */}
            <Card>
              <CardHeader>
                <CardTitle>System Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="font-medium">{systemInfo.hardware.systemType}</div>
                  <div className="text-muted-foreground">{systemInfo.hardware.penAndTouch}</div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6 mt-0">
            {/* System Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap size={20} />
                  System Performance
                </CardTitle>
                <CardDescription>Real-time system resource usage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* CPU Usage */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Cpu size={16} />
                      CPU Usage
                    </span>
                    <span className="text-sm text-muted-foreground">{systemInfo.performance.cpuUsage}%</span>
                  </div>
                  <Progress value={systemInfo.performance.cpuUsage} className="h-2" />
                </div>

                {/* Memory Usage */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <MemoryStick size={16} />
                      Memory Usage
                    </span>
                    <span className="text-sm text-muted-foreground">{systemInfo.performance.memoryUsage}%</span>
                  </div>
                  <Progress value={systemInfo.performance.memoryUsage} className="h-2" />
                </div>

                {/* Disk Usage */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <HardDrive size={16} />
                      Disk Usage
                    </span>
                    <span className="text-sm text-muted-foreground">{systemInfo.performance.diskUsage}%</span>
                  </div>
                  <Progress value={systemInfo.performance.diskUsage} className="h-2" />
                </div>
              </CardContent>
            </Card>

            {/* System Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Thermometer size={20} />
                  System Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">CPU Temperature:</span>
                    <div className="font-medium">{systemInfo.performance.temperature}°C</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">System Uptime:</span>
                    <div className="font-medium">{systemInfo.performance.uptime}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="space-y-6 mt-0">
            {/* System Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings size={20} />
                  Advanced System Settings
                </CardTitle>
                <CardDescription>Configure advanced system options</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="justify-start">
                    Environment Variables
                  </Button>
                  <Button variant="outline" className="justify-start">
                    Device Manager
                  </Button>
                  <Button variant="outline" className="justify-start">
                    System Protection
                  </Button>
                  <Button variant="outline" className="justify-start">
                    Remote Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* System Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info size={20} />
                  Detailed System Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-muted-foreground">BIOS Version:</span>
                    <div className="font-mono">UEFI 2.7.0</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Secure Boot:</span>
                    <div className="font-medium text-green-600">Enabled</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">TPM Version:</span>
                    <div className="font-mono">2.0</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Boot:</span>
                    <div className="font-medium">{currentTime}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="border-t p-4 bg-muted/30 flex-shrink-0">
          <div className="flex justify-end gap-2">
            <Button variant="outline">
              Export System Info
            </Button>
            <Button>
              OK
            </Button>
          </div>
        </div>
      </Tabs>
    </div>
  );
};